import React, {useEffect, useState} from 'react';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {Modal} from './Modal';
import {ActivityButton} from './ActivityButton';
import ReactSelect from 'react-select/creatable';
import {WorkflowSearchFormState} from './WorkflowSearchForm';

type WorkflowSavedSearchProps = {
    workflowId: string;
    displayColumns: any;
    formState: any;
    setFormState: (state: WorkflowSearchFormState) => void;
    onSearch: (state: WorkflowSearchFormState, columns: any) => void;
    initialFormState: WorkflowSearchFormState;
    isDirty: boolean;
    setIsDirty: (isDirty: boolean) => void;
};

type SaveSearchState = {
    showModal: boolean;
    id: number;
    name: string;
    isSaving: boolean;
    saveAsNew: boolean;
    saveAsPublic: boolean;
    errorMessage: string;
};

const initialSaveSearchState: SaveSearchState = {
    showModal: false,
    isSaving: false,
    id: 0,
    name: '',
    saveAsNew: false,
    saveAsPublic: false,
    errorMessage: ''
};

export const WorkflowSavedSearch: React.FunctionComponent<WorkflowSavedSearchProps> = ({
                                                                                           workflowId,
                                                                                           formState,
                                                                                           displayColumns,
                                                                                           setFormState,
                                                                                           onSearch,
                                                                                           initialFormState,
                                                                                           isDirty,
                                                                                           setIsDirty

                                                                                       }) => {
    const [savedSearches, setSavedSearches] = useState<any>([]);
    const [saveSearchState, setSaveSearchState] = useState<SaveSearchState>({...initialSaveSearchState});
    const [savedSearchesKey, setSavedSearchesKey] = useState<number>(1);
    const [selectedSaveSearch, setSelectedSaveSearch] = useState<any>();
    const [isDeletingSearch, setIsDeletingSearch] = useState<boolean>(false);

    const makeSavedSearchOption = (item: any) => ({
        value: item.id,
        label: item.name,
        state: {
            ...item.state,
            form: {
                ...item.state.form,
                startDate: item.state.form.startDate ? new Date(item.state.form.startDate) : null,
                endDate: item.state.form.endDate ? new Date(item.state.form.endDate) : null
            }
        },
        isPublic: (item.is_public === 'Y')
    });

    useEffect(() => {
        Flow.getInstance().getSavedSearches(workflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setSavedSearches([
                        {
                            label: 'My Saved Searches',
                            options: response.json.searches
                                .filter((search: any) => search.is_public === 'N')
                                .map(makeSavedSearchOption)
                        }, {
                            label: 'Public Saved Searches',
                            options: response.json.searches
                                .filter((search: any) => search.is_public === 'Y')
                                .map(makeSavedSearchOption)
                        }
                    ]);
                } else {
                    throw Error(response?.json?.message || 'Error fetching saved searches');
                }
            })
            .catch((error: any) => {
                console.log(error);
            })

    }, [workflowId, savedSearchesKey]);

    const saveSearch = (formState: any, workflowId: string, name: string, savedSearchId: number, saveAsPublic: boolean) => {
        setSaveSearchState({
            ...saveSearchState,
            isSaving: true
        });

        Flow.getInstance().saveSearch(formState, workflowId, name, savedSearchId, saveAsPublic)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setSaveSearchState((state) => ({
                        ...state,
                        showModal: false
                    }));
                    setSavedSearchesKey(savedSearchesKey + 1);
                    setSelectedSaveSearch(makeSavedSearchOption({
                        id: response.json,
                        is_public: saveAsPublic,
                        name,
                        state: formState,
                        workflow_id: workflowId
                    }));
                    setIsDirty(false);
                } else {
                    throw Error(response?.json?.message || 'Error saving search');
                }
            })
            .catch((error) => {
                setSaveSearchState((state) => ({
                    ...state,
                    errorMessage: error?.message || error || 'Error saving search'
                }));
            })
            .finally(() => {
                setSaveSearchState((state) => ({
                    ...state,
                    isSaving: false
                }));
            });
    };

    const deletedSavedSearch = (savedSearchId: number) => {
        setIsDeletingSearch(true);

        Flow.getInstance().deleteSearch(savedSearchId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setSavedSearchesKey(savedSearchesKey + 1);
                } else {
                    throw Error(response?.json?.message || 'Error deleting search');
                }
            })
            .catch((error) => {
                setSaveSearchState((state) => ({
                    ...state,
                    errorMessage: error?.message || error || 'Error deleting search'
                }));
            })
            .finally(() => {
                setIsDeletingSearch(false);
            });
    }

    return (
        <div>
            {console.log('Selected Save Search', selectedSaveSearch)}
            {workflowId &&
            <div className="flex-grow-1">
                <div className="float-right d-flex flex-column" style={{minWidth: '250px'}}>
                    <div>
                        <ReactSelect
                            placeholder="Saved Searches"
                            isSearchable={true}
                            isClearable={true}
                            options={savedSearches}
                            value={selectedSaveSearch}
                            onChange={(option: any, type: any) => {
                                if (type.action === 'clear') {
                                    setSaveSearchState({...initialSaveSearchState});
                                    const newFormState = {
                                        ...initialFormState,
                                        workflowId: formState.workflowId,
                                        workflow: formState.workflow
                                    };
                                    setFormState(newFormState);
                                    onSearch && onSearch(newFormState, null);
                                    setSelectedSaveSearch(undefined);
                                } else {
                                    if (option) {
                                        setSaveSearchState({
                                            ...saveSearchState,
                                            id: option.value,
                                            name: option.label,
                                            saveAsPublic: option.isPublic,
                                        });
                                        setFormState(option.state.form);
                                        onSearch && onSearch(option.state.form, option.state.columns);
                                        setSelectedSaveSearch(option);
                                        setIsDirty(false);
                                    }
                                }
                            }}
                        />
                    </div>
                    <div className="d-flex justify-content-between">
                        <ActivityButton type="button"
                                        className="btn btn-link text-danger"
                                        style={{paddingLeft: '0'}}
                                        disabled={isDeletingSearch || !selectedSaveSearch?.value}
                                        isActive={isDeletingSearch}
                                        onClick={() => {
                                            deletedSavedSearch(selectedSaveSearch?.value);
                                            setSelectedSaveSearch(null);
                                        }}
                        >Delete
                        </ActivityButton>
                        <button type="button"
                                className="btn btn-link"
                                style={{paddingRight: '0'}}
                                disabled={!isDirty}
                                onClick={() => {
                                    setSaveSearchState({
                                        ...saveSearchState,
                                        showModal: true
                                    });
                                }}
                        >Save Current Search
                        </button>
                    </div>
                </div>
            </div>
            }
            {saveSearchState.showModal &&
            <Modal>
                <div className="modal-header">
                    <h5 className="modal-title">Save Search</h5>
                    <button type="button"
                            className="close"
                            aria-label="Close"
                            tabIndex={-1}
                            onClick={() => {
                                setSaveSearchState({
                                    ...saveSearchState,
                                    showModal: false
                                });
                            }}
                    >
                        <span aria-hidden="true">×</span>
                    </button>
                </div>
                <div className="modal-body">
                    {saveSearchState.errorMessage && <p className="alert alert-danger">{saveSearchState.errorMessage}</p>}
                    <form>
                        <div className="form-group">
                            <label className="control-label">Name</label>
                            <input type="text"
                                   className="form-control"
                                   value={saveSearchState.name}
                                   onChange={(e) => {
                                       setSaveSearchState({
                                           ...saveSearchState,
                                           name: e.target.value
                                       })
                                   }}
                            />
                        </div>
                        <div className="form-check">
                            <input type="checkbox"
                                   id="save-as-new-search"
                                   className="form-check-input"
                                   checked={!saveSearchState.id || saveSearchState.saveAsNew}
                                   disabled={!saveSearchState.id}
                                   onChange={(e) => {
                                       setSaveSearchState({
                                           ...saveSearchState,
                                           saveAsNew: e.target.checked
                                       })
                                   }}
                            />
                            <label className="form-check-label"
                                   htmlFor="save-as-new-search"
                            >Save As New Search?</label>
                        </div>
                        {/*
                        <div className="form-check">
                            <input type="checkbox"
                                   id="save-as-public"
                                   className="form-check-input"
                                   checked={saveSearchState.saveAsPublic}
                                   onChange={(e) => {
                                       setSaveSearchState({
                                           ...saveSearchState,
                                           saveAsPublic: e.target.checked
                                       })
                                   }}
                            />
                            <label className="form-check-label"
                                   htmlFor="save-as-public"
                            >Save Publicly?</label>
                        </div>
                        */}
                    </form>
                </div>
                <div className="modal-footer">
                    <button type="button"
                            className="btn btn-secondary"
                            disabled={saveSearchState.isSaving}
                            onClick={() => {
                                setSaveSearchState({
                                    ...saveSearchState,
                                    showModal: false
                                });
                            }}
                    >Cancel
                    </button>
                    <ActivityButton
                        className="btn btn-primary"
                        isActive={saveSearchState.isSaving}
                        onClick={() => {
                            saveSearch(
                                {form: formState, columns: displayColumns},
                                workflowId, saveSearchState.name,
                                saveSearchState.saveAsNew ? 0 : saveSearchState.id,
                                saveSearchState.saveAsPublic
                            );
                        }}
                    >Save</ActivityButton>
                </div>
            </Modal>
            }
        </div>
    )
}